import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { todayKey, toPaise, type AllocationPreview } from "./installmentMath";

/**
 * "Which instalment is this payment for?"
 *
 * Required on any deal that has a plan — money must be attributed, because a
 * schedule whose rows never get settled is worse than no schedule: it keeps
 * nagging for amounts the customer has already paid.
 *
 * Built as a click-outside dropdown rather than a Radix Select to match the
 * neighbouring PaymentMethodDropdown, and because this form already lives
 * inside a Sheet inside a Dialog and every extra portal layer there is a new
 * z-index argument.
 */

export interface PickableInstallment {
  id: number;
  seq: number;
  dueDate: string;
  outstanding: number;
}

function money(n: number): string {
  const hasPaise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

interface Props {
  installments: PickableInstallment[];
  selectedId: number | null;
  onSelect: (inst: PickableInstallment) => void;
  /** Rendered under the control once an amount has been typed. */
  allocation?: AllocationPreview | null;
  /** The amount currently in the form, for the spill copy. */
  amount?: number;
  /** Offered when the typed amount would clear the whole schedule. */
  onForeclose?: () => void;
}

export default function InstallmentPicker({
  installments,
  selectedId,
  onSelect,
  allocation,
  amount = 0,
  onForeclose,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const today = todayKey();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const selected = installments.find((i) => i.id === selectedId) ?? null;

  /**
   * Overdue rows first, then by sequence — the default selection should be the
   * money that is most late, not merely the lowest number.
   */
  const ordered = useMemo(() => {
    const isOverdue = (i: PickableInstallment) => i.dueDate.slice(0, 10) < today;
    return [...installments].sort((a, b) => {
      const d = Number(isOverdue(b)) - Number(isOverdue(a));
      return d !== 0 ? d : a.seq - b.seq;
    });
  }, [installments, today]);

  // Preselect on mount / when the ladder changes, so the operator never has to
  // pick in the common case.
  useEffect(() => {
    if (selectedId == null && ordered.length > 0) onSelect(ordered[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordered.length]);

  const spillsForward = (allocation?.slices.length ?? 0) > 1;

  // Does this amount clear everything that is left? If so, part-paying six rows
  // one at a time is the wrong shape for what the customer is actually doing —
  // they are settling the plan. Offer that instead of quietly doing the long
  // version, which leaves the schedule technically open and still reminding.
  const totalOutstanding = installments.reduce((s, i) => s + i.outstanding, 0);
  const clearsEverything =
    amount > 0 && toPaise(amount) >= toPaise(totalOutstanding);
  const shortfall =
    selected && amount > 0 && amount < selected.outstanding
      ? selected.outstanding - amount
      : 0;

  return (
    <div className="flex flex-col gap-1" ref={ref}>
      <label className="text-[10px] font-medium text-[#697571] uppercase tracking-[0.04em]">
        Applying to
      </label>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-[10px] border border-[#e2e4e3] bg-white hover:bg-[#f7f9f8] transition-colors text-left"
      >
        {selected ? (
          <span className="text-[12px] text-[#1e302a] truncate tabular-nums">
            #{selected.seq} · due {shortDate(selected.dueDate)} ·{" "}
            <span className="text-[#697571]">
              {money(selected.outstanding)} left
            </span>
          </span>
        ) : (
          <span className="text-[12px] text-[#9ba3a0]">Pick an instalment</span>
        )}
        <ChevronDown
          className={`size-3.5 text-[#697571] shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="relative">
          <div className="absolute z-20 mt-1 w-full max-h-[220px] overflow-y-auto rounded-[10px] border border-[#e2e4e3] bg-white shadow-lg">
            {ordered.map((i) => {
              const overdue = i.dueDate.slice(0, 10) < today;
              const active = i.id === selectedId;
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => {
                    onSelect(i);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-[#f7f9f8] transition-colors ${
                    active ? "bg-[#f2fbf7]" : ""
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] text-[#9ba3a0] tabular-nums w-6 shrink-0">
                      #{i.seq}
                    </span>
                    <span className="text-[12px] text-[#374741] truncate">
                      {shortDate(i.dueDate)}
                    </span>
                    {overdue && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-[#fdeaed] text-[#da2a46] shrink-0">
                        Overdue
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[11px] text-[#697571] tabular-nums">
                      {money(i.outstanding)}
                    </span>
                    {active && <Check className="size-3 text-[#218760]" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* What the entered amount will actually do. Shown BEFORE saving, because
          "where did my ₹50,000 go" is not a question anyone should have to ask
          afterwards. */}
      {clearsEverything && onForeclose && (
        <div className="mt-1 flex items-start gap-2 rounded-lg bg-[#f0faf5] border border-[#c1e1d5] px-2.5 py-1.5">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-[#1a6b4c] leading-[1.45]">
              This covers every remaining instalment. Foreclose instead — it
              closes the schedule so the future dates stop reminding you.
            </p>
          </div>
          <button
            type="button"
            onClick={onForeclose}
            className="shrink-0 rounded-lg bg-[#218760] px-2 py-1 text-[10px] font-medium text-white hover:bg-[#1a6b4c] transition-colors"
          >
            Foreclose
          </button>
        </div>
      )}

      {selected && amount > 0 && (
        <div className="mt-0.5">
          {spillsForward ? (
            <p className="text-[10px] text-[#8a5a2b] leading-[1.5]">
              Settles{" "}
              {allocation!.slices
                .map((s) => `#${s.seq} (${money(s.amount)})`)
                .join(", ")}
              .
              {allocation!.unallocated > 0 && (
                <> {money(allocation!.unallocated)} stays unadjusted.</>
              )}
            </p>
          ) : allocation && allocation.unallocated > 0 ? (
            <p className="text-[10px] text-[#2b4a8a] leading-[1.5]">
              Settles instalment #{selected.seq}.{" "}
              {money(allocation.unallocated)} is more than the schedule still
              owes and stays unadjusted on the deal.
            </p>
          ) : shortfall > 0 ? (
            <p className="text-[10px] text-[#8a6d00] leading-[1.5]">
              Part-pays #{selected.seq} — {money(shortfall)} would still be due
              on {shortDate(selected.dueDate)}.
            </p>
          ) : (
            <p className="text-[10px] text-[#156548] leading-[1.5]">
              Settles instalment #{selected.seq} in full.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
